import {
  Dialog,
  Select,
  MenuItem,
  InputBase,
  Button,
  CircularProgress,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import axios from "axios";
import posthog from "posthog-js";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building, Phone, User, UserCircle, X } from "lucide-react";
import { useOrgUsers } from "../../hooks/useOrgUsers";

const AddContactModal = ({
  open,
  setOpen,
  setOpenUserInvitedModal,
  users,
  setUsers,
  planDetails,
  loader,
  setLoader,
  setOpenContactAddedModal,
  setName,
  onContactAdded
}) => {
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState(null);

  const [user, setUser] = useState({
    name: "",
    company: "",
    phoneNumber: "",
    source: "manual",
    assignedUserId: "",
    organizationId: JSON.parse(localStorage.getItem("organization")).orgId.S,
  });

  const { users: orgUsers, loading: orgUsersLoading } = useOrgUsers(
    user.organizationId
  );

  // Auto-format as (123) 456-7890
function formatUSPhone(input) {
    const digits = input.replace(/\D/g, "").substring(0, 10);
  
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // Convert formatted input → +1XXXXXXXXXX
  function toE164(formatted) {
    const digits = formatted.replace(/\D/g, "");
    if (digits.length !== 10) return null;
    return `+1${digits}`;
  }
  
  // Validate US phone number
  function isValidUSPhone(formatted) {
    const digits = formatted.replace(/\D/g, "");
    return digits.length === 10;
  }

  const createUser = async () => {
    if (!isValidUSPhone(user.phoneNumber)) {
      setErrorMsg(
        "Please enter a valid phone number (e.g. (555) 123-4567)."
      );
      return;
    }

    if (user.name.length <= 1 ) {
        setErrorMsg(
          "Please enter a valid name."
        );
        return;
      }

      if ( user.company.length <= 1) {
        setErrorMsg(
          "Please enter a valid company."
        );
        return;
      }

    

 
    setLoader(true);

    try {
      const check = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        {
          userId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );

      const user___ = check.data.user;

      if (
        !user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        window.location.reload();
        return;
      }

      const e164Phone = toE164(user.phoneNumber);


      if (!e164Phone) {
        setErrorMsg("Phone number is invalid.");
        return;
      }


     
      
      const add_contact = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "add-contact",
        {
          name: user.name,
          company: user.company,
          phoneNumber: e164Phone,
          source: "manual",
          assignedUserId: user.assignedUserId || null,
          organizationId: JSON.parse(localStorage.getItem("organization")).orgId
            .S,
        }
      );

      // Refresh contacts list via callback if provided
      if (onContactAdded) {
        onContactAdded();
      }

      // Keep setUsers for backward compatibility (if still needed)
      if (setUsers) {
        setUsers((prev) => [
          ...prev,
          {
            name: `${add_contact.data.contact.name}`,
            company: add_contact.data.contact.company,
            phoneNumber: add_contact.data.contact.phoneNumber,
            source: "manual",
            organizationId: JSON.parse(localStorage.getItem("organization")).orgId
              .S,
          },
        ]);
      }

      setOpen(false);

      setUser({
        name: "",
        company: "",
        phoneNumber: "",
        source: "manual",
        assignedUserId: "",
        organizationId: JSON.parse(localStorage.getItem("organization")).orgId
          .S,
      });
      setOpenContactAddedModal(true);
      setName(add_contact.data.contact.name);
    } catch (err) {
      // Log for production diagnostics when add contact fails
      console.error("AddContactModal: add contact failed", err.response?.data || err.message || err);
      setErrorMsg(err.response?.data?.message);
    }

    setLoader(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth={false}
      maxWidth="md"
      style={{ borderRadius: "10px" }}
      BackdropProps={{
        style: { backgroundColor: "rgb(144,150,160,0.6)" },
      }}
      disableEnforceFocus
    >
      <div className="px-6 py-4 lg:w-[400px] w-[80vw] ">
        {/* Title */}
        <div className="flex items-center justify-between  mb-3">
          <div className="text-[17px] font-semibold  ">Add New Contact</div>
          <X
            className="w-[18px] text-gray-400 "
            onClick={() => setOpen(false)}
          />
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="text-red-600 text-sm mb-4">{errorMsg}</div>
        )}

        {/* User Type */}
        <div className="mb-4">
          <div className="text-[13px] font-semibold mb-2 flex items-center">
            {" "}
            <User className="w-[16px]" /> Full Name *
          </div>
          <InputBase
            placeholder="John Doe"
            value={user.name}
            onChange={(e) => setUser({ ...user, name: e.target.value })}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              px: 2,
              py: 0.5,
              width: "100%",
              borderRadius: "6px",
              fontSize: "15px",
              fontWeight: "500",
            }}
          />
        </div>

        <div className="mb-4">
          <div className="text-[13px] font-semibold mb-2 flex items-center">
            {" "}
            <Phone className="w-[16px]" /> Phone Number *
          </div>
          <InputBase
            placeholder="(555) 123-4567"
            value={user.phoneNumber}
            onChange={(e) => {
              const formatted = formatUSPhone(e.target.value);
              setUser({ ...user, phoneNumber: formatted });
            }}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              px: 2,
              py: 0.5,
              width: "100%",
              fontSize: "15px",
              fontWeight: "500",
            }}
          />    
        </div>

        <div className="mb-4">
          <div className="text-[13px] font-semibold mb-2 flex items-center">
            {" "}
            <Building className="w-[16px]" /> Company *
          </div>
          <InputBase
            placeholder="ACME Inc."
            value={user.company}
            onChange={(e) => setUser({ ...user, company: e.target.value })}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              px: 2,
              py: 0.5,
              width: "100%",
              borderRadius: "6px",
              fontSize: "15px",
              fontWeight: "500",
            }}
          />
        </div>

        <div className="mb-4">
          <div className="text-[13px] font-semibold mb-2 flex items-center gap-1">
            <UserCircle className="w-[16px]" /> Assigned to
          </div>
          <Select
            displayEmpty
            value={user.assignedUserId}
            onChange={(e) =>
              setUser({ ...user, assignedUserId: e.target.value })
            }
            disabled={orgUsersLoading}
            IconComponent={KeyboardArrowDown}
            input={
              <InputBase
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  px: 2,
                  py: 0.5,
                  width: "100%",
                  fontSize: "15px",
                  fontWeight: "500",
                }}
              />
            }
            sx={{ width: "100%" }}
          >
            <MenuItem value="">
              <em>Unassigned</em>
            </MenuItem>
            {orgUsers.map((orgUser) => (
              <MenuItem key={orgUser.user_id} value={orgUser.user_id}>
                {orgUser.name || orgUser.email}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            onClick={() => {
                setOpen(false)
                setUser({
    name: "",
    company: "",
    phoneNumber: "",
    source: "manual",
    organizationId: JSON.parse(localStorage.getItem("organization")).orgId.S,
  })

  setErrorMsg(null);
            }}
            sx={{
              textTransform: "none",
              bgcolor: "white",
              color: "#374151",
              border: "1px solid #D1D5DB",
              px: 2,
              py: 1,
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={createUser}
            disabled={loader}
            sx={{
              px: 1,
              py: 1,
              borderRadius: "8px",
            }}
          >
            {loader ? (
              <CircularProgress size="1.3rem" sx={{ color: "white" }} />
            ) : (
              "Add Contact"
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default AddContactModal;
